import React, { useState, useRef } from "react";
import { Terminal, GameController, Sparkle, Circle } from "@phosphor-icons/react";
import { AsciiFlappyBird } from "./AsciiFlappyBird";
import { GhosttyShell } from "./GhosttyShell";
import { MatrixRain } from "./MatrixRain";

export const GhosttyTerminal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"flappy" | "shell" | "matrix">("flappy");
  const terminalRef = useRef<HTMLDivElement | null>(null);

  const handleTabChange = (tab: "flappy" | "shell" | "matrix") => {
    setActiveTab(tab);
    if (tab === "shell") {
      // Calibrated gentle adjustment: bring the terminal into comfortable view below nav
      requestAnimationFrame(() => {
        if (terminalRef.current) {
          const rect = terminalRef.current.getBoundingClientRect();
          const idealTop = window.innerWidth < 640 ? 75 : 110;
          const offset = rect.top - idealTop;
          if (Math.abs(offset) > 25) {
            window.scrollBy({
              top: offset,
              behavior: "smooth",
            });
          }
        }
      });
    }
  };

  return (
    <div
      ref={terminalRef}
      className="w-full rounded-2xl bg-[#181716] border border-[#2A2926] shadow-2xl overflow-hidden flex flex-col transition-all duration-300"
    >
      {/* Ghostty Top Window Chrome & Tab Bar */}
      <div className="flex flex-wrap items-center justify-between px-3.5 sm:px-4 py-2.5 bg-[#1F1E1B] border-b border-[#282724] gap-2">
        {/* Left: Traffic Lights & Ghostty Title */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-[#FF5F56] border border-[#E0443E]/50 block" />
            <span className="w-3 h-3 rounded-full bg-[#FFBD2E] border border-[#DEA123]/50 block" />
            <span className="w-3 h-3 rounded-full bg-[#27C93F] border border-[#1AAB29]/50 block" />
          </div>

          <div className="hidden sm:flex items-center gap-1.5 text-xs font-mono text-[#8C8A84] border-l border-[#2E2C28] pl-3">
            <Terminal size={13} className="text-[#E5983A]" />
            <span>roustaie@tehran:~</span>
          </div>
        </div>

        {/* Center: Window Tabs */}
        <div className="flex items-center gap-1 bg-[#141413] p-0.5 rounded-xl border border-[#2B2A27]">
          <button
            onClick={() => handleTabChange("flappy")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-mono transition cursor-pointer ${
              activeTab === "flappy"
                ? "bg-[#252421] text-[#F3F2EE] border border-[#3E3C37] shadow-sm"
                : "text-[#8C8A84] hover:text-[#DCD8CE] hover:bg-[#1A1917]"
            }`}
          >
            <GameController size={13} className={activeTab === "flappy" ? "text-[#E5983A]" : ""} />
            <span>flappy.sh</span>
          </button>

          <button
            onClick={() => handleTabChange("shell")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-mono transition cursor-pointer ${
              activeTab === "shell"
                ? "bg-[#252421] text-[#F3F2EE] border border-[#3E3C37] shadow-sm"
                : "text-[#8C8A84] hover:text-[#DCD8CE] hover:bg-[#1A1917]"
            }`}
          >
            <Terminal size={13} className={activeTab === "shell" ? "text-[#E5983A]" : ""} />
            <span>shell</span>
          </button>

          <button
            onClick={() => handleTabChange("matrix")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-mono transition cursor-pointer ${
              activeTab === "matrix"
                ? "bg-[#252421] text-[#F3F2EE] border border-[#3E3C37] shadow-sm"
                : "text-[#8C8A84] hover:text-[#DCD8CE] hover:bg-[#1A1917]"
            }`}
          >
            <Sparkle size={13} className={activeTab === "matrix" ? "text-[#E5983A]" : ""} />
            <span>matrix.sh</span>
          </button>
        </div>

        {/* Right: Ghostty Engine Badge */}
        <div className="hidden md:flex items-center gap-2 text-[11px] font-mono text-[#75736D]">
          <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80]" />
          <span>GHOSTTY 1.1.2</span>
        </div>
      </div>

      {/* Tab Viewport */}
      <div className="w-full bg-[#121110]">
        {activeTab === "flappy" && <AsciiFlappyBird />}
        {activeTab === "shell" && <GhosttyShell onSwitchTab={setActiveTab} />}
        {activeTab === "matrix" && <MatrixRain />}
      </div>

      {/* Bottom Status Ribbon */}
      <div className="px-4 py-2 bg-[#171615] border-t border-[#242320] flex flex-wrap items-center justify-between gap-2 text-xs font-mono text-[#8C8A84]">
        <div className="flex items-center gap-3">
          <span className="text-[#E5983A]">NORMAL</span>
          <span className="text-[#4E4C46]">|</span>
          <span>UTF-8</span>
          <span className="text-[#4E4C46]">|</span>
          <span>zsh / xterm-256color</span>
        </div>

        <div className="flex items-center gap-2 text-[11px]">
          <span>TEH, IRN · IRST</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#E5983A]" />
          <span>ONLINE</span>
        </div>
      </div>
    </div>
  );
};
